import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { API_PATH, request } from '../../config/api';
import styles from './ContactForm.module.css';

/**
 * Reusable contact form (name, email, mobile, subject, message) with validation and send API.
 * @param {Object} props
 * @param {() => void} props.onSuccess - Called after message is sent successfully
 * @param {string} [props.defaultSubject] - Prefill subject (e.g. "Request demo")
 * @param {string} [props.title] - Optional heading above form
 * @param {string} [props.className] - Wrapper class name
 */
const ContactForm = ({ onSuccess, defaultSubject = '', title = 'Send us a message', className = '' }) => {
    const [form, setForm] = useState({
        name: '',
        email: '',
        mobile: '',
        subject: defaultSubject,
        message: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const err = {};
        const name = (form.name || '').trim();
        const email = (form.email || '').trim();
        const mobile = (form.mobile || '').replace(/\s/g, '');
        const subject = (form.subject || '').trim();
        const message = (form.message || '').trim();
        if (name.length < 2) err.name = 'Name must be at least 2 characters';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = 'Enter a valid email address';
        if (!/^[0-9]{10}$/.test(mobile)) err.mobile = 'Enter a valid 10-digit mobile number';
        if (subject.length < 3) err.subject = 'Subject must be at least 3 characters';
        if (message.length < 10) err.message = 'Message must be at least 10 characters';
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        if (!validate()) return;
        setLoading(true);
        try {
            const res = await request(`${API_PATH}/contact/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim().toLowerCase(),
                    mobile: form.mobile.replace(/\s/g, ''),
                    subject: form.subject.trim(),
                    message: form.message.trim(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErrors({ submit: data.error || 'Failed to send message' });
                return;
            }
            setForm({ name: '', email: '', mobile: '', subject: defaultSubject, message: '' });
            setErrors({});
            onSuccess?.();
        } catch {
            setErrors({ submit: 'Network error. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${styles.wrap} ${className}`}>
            {title && <h3 className={styles.title}>{title}</h3>}
            {errors.submit && (
                <div className={styles.error} role="alert">
                    <AlertCircle size={16} className={styles.errorIcon} aria-hidden="true" />
                    <span>{errors.submit}</span>
                </div>
            )}
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.row}>
                    <label className={styles.label} htmlFor="cf-name">Name</label>
                    <input
                        id="cf-name"
                        type="text"
                        className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                        placeholder="Your name"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        onBlur={() => errors.name && validate()}
                        autoComplete="name"
                        maxLength={100}
                    />
                    {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                </div>
                <div className={styles.row}>
                    <label className={styles.label} htmlFor="cf-email">Email</label>
                    <input
                        id="cf-email"
                        type="email"
                        className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        onBlur={() => errors.email && validate()}
                        autoComplete="email"
                    />
                    {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                </div>
                <div className={styles.row}>
                    <label className={styles.label} htmlFor="cf-mobile">Mobile</label>
                    <input
                        id="cf-mobile"
                        type="tel"
                        className={`${styles.input} ${errors.mobile ? styles.inputError : ''}`}
                        placeholder="10-digit number"
                        value={form.mobile}
                        onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                        onBlur={() => errors.mobile && validate()}
                        autoComplete="tel"
                        maxLength={10}
                    />
                    {errors.mobile && <span className={styles.errorText}>{errors.mobile}</span>}
                </div>
                <div className={styles.row}>
                    <label className={styles.label} htmlFor="cf-subject">Subject</label>
                    <input
                        id="cf-subject"
                        type="text"
                        className={`${styles.input} ${errors.subject ? styles.inputError : ''}`}
                        placeholder="What is this regarding?"
                        value={form.subject}
                        onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                        onBlur={() => errors.subject && validate()}
                        maxLength={200}
                    />
                    {errors.subject && <span className={styles.errorText}>{errors.subject}</span>}
                </div>
                <div className={styles.row}>
                    <label className={styles.label} htmlFor="cf-message">Message</label>
                    <textarea
                        id="cf-message"
                        className={`${styles.input} ${styles.textarea} ${errors.message ? styles.inputError : ''}`}
                        placeholder="Your message..."
                        value={form.message}
                        onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                        onBlur={() => errors.message && validate()}
                        rows={4}
                        maxLength={2000}
                    />
                    {errors.message && <span className={styles.errorText}>{errors.message}</span>}
                </div>
                <button type="submit" className={styles.submit} disabled={loading}>
                    {loading ? 'Sending…' : 'Send message'}
                </button>
            </form>
        </div>
    );
};

export default ContactForm;
